import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  count?: number;
}

interface PremiumDropdownProps {
  label: string;
  icon: React.ReactNode;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export default function PremiumDropdown({ 
  label, 
  icon, 
  options, 
  value, 
  onChange, 
  placeholder 
}: PremiumDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          group flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl
          border transition-all duration-300 min-w-[140px] sm:min-w-[180px] flex-shrink-0
          ${isOpen 
            ? 'border-coral/50 bg-coral/5' 
            : value 
              ? 'border-coral/30 bg-coral/5' 
              : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
          }
        `}
      >
        <span className={`transition-colors duration-200 ${value ? 'text-coral' : 'text-white/40 group-hover:text-white/60'}`}>
          {icon}
        </span>
        <div className="flex flex-col items-start flex-1">
          <span className="text-[10px] uppercase tracking-wider text-white/30 font-medium">
            {label}
          </span>
          <span className={`text-sm font-medium ${value ? 'text-white' : 'text-white/60'}`}>
            {selectedOption?.label || placeholder}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className={`w-4 h-4 transition-colors ${isOpen ? 'text-coral' : 'text-white/30'}`} />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute top-full left-0 right-0 mt-2 z-50 
                       rounded-xl overflow-hidden
                       border border-white/10 shadow-2xl"
            style={{
              background: 'linear-gradient(180deg, rgba(20, 20, 25, 0.98) 0%, rgba(15, 15, 20, 0.98) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="max-h-[280px] overflow-y-auto scrollbar-hide py-1">
              {options.map((option, index) => (
                <motion.button
                  key={option.value}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center justify-between px-4 py-3
                    transition-all duration-150 text-left relative
                    ${option.value === value 
                      ? 'bg-coral/10 text-coral' 
                      : 'text-white/70 hover:bg-white/[0.05] hover:text-white'
                    }
                  `}
                >
                  <span className="text-sm font-medium">{option.label}</span>
                  {option.count !== undefined && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      option.value === value 
                        ? 'bg-coral/20 text-coral' 
                        : 'bg-white/5 text-white/40'
                    }`}>
                      {option.count}
                    </span>
                  )}
                  {option.value === value && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute left-0 w-0.5 h-6 bg-coral rounded-r-full"
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

